import { useEffect, useState, useRef, type MouseEvent, type ChangeEvent } from 'react';
import Modal from '@/components/base/Modal';
import {
  fetchPurchaseDetail,
  cancelPurchase,
  uploadPurchaseReceipt,
  updatePurchaseReceipt,
  PURCHASE_STATUS_LABELS,
  type PurchaseDetail,
} from '@/services/purchases';
import { downloadTextFile } from '@/lib/download';
import PurchaseStatusBadge from './PurchaseStatusBadge';
import PurchasePaymentsSection from './PurchasePaymentsSection';

interface PurchaseDetailModalProps {
  purchaseId: string;
  onClose: () => void;
}

function formatDate(iso: string): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('es-CO', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function buildPurchaseText(detail: PurchaseDetail): string {
  const line = '----------------------------------------------';
  const lines: string[] = [];
  lines.push(line);
  lines.push('  Comprobante de Compra');
  lines.push(`  Factura: ${detail.invoiceNumber || 'Sin factura'}`);
  lines.push(line);
  lines.push('');
  lines.push(`Fecha: ${formatDate(detail.createdAt)}`);
  lines.push(`Proveedor: ${detail.supplierName || '—'}`);
  lines.push(`Estado: ${PURCHASE_STATUS_LABELS[detail.status]}`);
  lines.push('');
  lines.push('INSUMOS');
  lines.push(line);
  if (detail.items.length === 0) {
    lines.push('Sin ítems registrados.');
  } else {
    detail.items.forEach((it) => {
      lines.push(
        `${it.productName || '—'}${it.sizeLabel ? ` · ${it.sizeLabel}` : ''}  x${it.quantity}  $${(it.quantity * it.unitCost).toLocaleString()}`,
      );
    });
  }
  lines.push(line);
  lines.push(`TOTAL INVERTIDO: $${detail.totalCost.toLocaleString()}`);
  lines.push(`ABONADO: $${detail.paidAmount.toLocaleString()}`);
  lines.push(`SALDO PENDIENTE: $${detail.balance.toLocaleString()}`);
  if (detail.payments.length > 0) {
    lines.push('');
    lines.push('ABONOS');
    lines.push(line);
    detail.payments.forEach((p) => {
      lines.push(`${formatDate(p.paidAt)}  $${p.amount.toLocaleString()}${p.notes ? `  (${p.notes})` : ''}`);
    });
  }
  if (detail.notes) {
    lines.push('');
    lines.push(`Notas: ${detail.notes}`);
  }
  lines.push(line);
  return lines.join('\n');
}

export default function PurchaseDetailModal({ purchaseId, onClose }: PurchaseDetailModalProps) {
  const [detail, setDetail] = useState<PurchaseDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [receiptError, setReceiptError] = useState('');
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [canceling, setCanceling] = useState(false);
  const [cancelError, setCancelError] = useState('');
  const fileRef = useRef<HTMLInputElement>(null);

  function reload() {
    fetchPurchaseDetail(purchaseId).then((d) => {
      setDetail(d);
      setLoading(false);
    });
  }

  useEffect(() => {
    let cancelled = false;
    fetchPurchaseDetail(purchaseId).then((d) => {
      if (!cancelled) {
        setDetail(d);
        setLoading(false);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [purchaseId]);

  function handleDownload(e: MouseEvent<HTMLButtonElement>) {
    e.preventDefault();
    if (!detail) return;
    const text = buildPurchaseText(detail);
    const datePart = new Date().toISOString().slice(0, 10).replace(/-/g, '');
    const safeInvoice = (detail.invoiceNumber || 'compra').replace(/[^a-zA-Z0-9-]/g, '_');
    downloadTextFile(`compra_${safeInvoice}_${datePart}.txt`, text);
  }

  async function handleReceipt(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setReceiptError('');
    setUploading(true);
    const { url, error: upError } = await uploadPurchaseReceipt(file);
    if (upError || !url) {
      setUploading(false);
      setReceiptError(upError || 'No se pudo subir el comprobante.');
      return;
    }
    const { error: saveError } = await updatePurchaseReceipt(purchaseId, url);
    setUploading(false);
    if (saveError) {
      setReceiptError(saveError);
      return;
    }
    reload();
  }

  async function handleRemoveReceipt() {
    setReceiptError('');
    const { error: saveError } = await updatePurchaseReceipt(purchaseId, '');
    if (saveError) {
      setReceiptError(saveError);
      return;
    }
    reload();
  }

  async function handleCancel() {
    setCancelError('');
    setCanceling(true);
    const { error: cancelErr } = await cancelPurchase(purchaseId);
    setCanceling(false);
    if (cancelErr) {
      setCancelError(cancelErr);
      return;
    }
    setConfirmCancel(false);
    reload();
  }

  const canCancel = detail && detail.status !== 'cancelled';

  return (
    <Modal
      title="Detalle de compra"
      subtitle={detail ? `Factura ${detail.invoiceNumber || 'Sin factura'}` : 'Cargando...'}
      onClose={onClose}
      maxWidth="max-w-lg"
    >
      {loading ? (
        <div className="text-center py-10 text-foreground-400 text-sm">
          <i className="ri-loader-4-line animate-spin mr-2"></i>Cargando...
        </div>
      ) : !detail ? (
        <div className="text-center py-10 text-foreground-500 text-sm">No se pudo cargar la compra.</div>
      ) : (
        <div className="space-y-4">
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <p className="text-xs text-foreground-500">Proveedor</p>
              <p className="text-sm font-medium text-foreground-900 truncate">{detail.supplierName || '—'}</p>
            </div>
            <PurchaseStatusBadge status={detail.status} />
          </div>

          {/* Resumen de pago */}
          <div className="grid grid-cols-3 gap-2">
            <div className="bg-background-50 border border-background-200 rounded-lg px-3 py-2.5">
              <p className="text-[11px] text-foreground-500">Total</p>
              <p className="text-sm font-bold text-foreground-950">${detail.totalCost.toLocaleString()}</p>
            </div>
            <div className="bg-emerald-50 border border-emerald-100 rounded-lg px-3 py-2.5">
              <p className="text-[11px] text-emerald-700">Abonado</p>
              <p className="text-sm font-bold text-emerald-700">${detail.paidAmount.toLocaleString()}</p>
            </div>
            <div className={`border rounded-lg px-3 py-2.5 ${detail.balance > 0 ? 'bg-red-50 border-red-100' : 'bg-background-50 border-background-200'}`}>
              <p className={`text-[11px] ${detail.balance > 0 ? 'text-red-700' : 'text-foreground-500'}`}>Saldo</p>
              <p className={`text-sm font-bold ${detail.balance > 0 ? 'text-red-700' : 'text-foreground-950'}`}>
                ${detail.balance.toLocaleString()}
              </p>
            </div>
          </div>

          {/* Insumos */}
          <div>
            <p className="text-xs font-semibold text-foreground-700 mb-2">Insumos comprados</p>
            {detail.items.length === 0 ? (
              <p className="text-sm text-foreground-400">Sin ítems registrados.</p>
            ) : (
              <div className="space-y-2 max-h-48 overflow-y-auto pr-1">
                {detail.items.map((it) => (
                  <div key={it.id} className="flex items-center gap-3 border border-background-200 rounded-lg px-3 py-2.5">
                    <div className="w-9 h-9 rounded-lg bg-accent-50 flex items-center justify-center flex-shrink-0">
                      <i className="ri-archive-line text-accent-600"></i>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-foreground-900">
                        {it.productName} {it.sizeLabel && `· ${it.sizeLabel}`}
                      </p>
                      <p className="text-xs text-foreground-500">
                        {it.quantity} × ${it.unitCost.toLocaleString()} compra · ${it.retailPrice.toLocaleString()} venta
                      </p>
                    </div>
                    <span className="text-sm font-semibold text-foreground-900 whitespace-nowrap">
                      ${(it.quantity * it.unitCost).toLocaleString()}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Abonos */}
          <div className="border-t border-background-200 pt-4">
            <PurchasePaymentsSection
              purchaseId={purchaseId}
              payments={detail.payments}
              balance={detail.balance}
              onChanged={reload}
            />
          </div>

          {/* Comprobante */}
          <div className="border-t border-background-200 pt-4 space-y-2">
            <p className="text-xs font-semibold text-foreground-700">Comprobante (opcional)</p>
            {receiptError && (
              <p className="text-xs text-red-600 flex items-center gap-1">
                <i className="ri-error-warning-line"></i>
                {receiptError}
              </p>
            )}
            {detail.receiptUrl ? (
              <div className="space-y-2">
                <img
                  src={detail.receiptUrl}
                  alt="Comprobante de compra"
                  className="w-full max-h-64 object-contain rounded-lg border border-background-200 bg-background-50"
                />
                <div className="flex gap-2">
                  <a
                    href={detail.receiptUrl}
                    target="_blank"
                    rel="noreferrer noopener"
                    className="flex-1 text-center text-xs px-3 py-2 border border-background-300 text-foreground-700 rounded-lg hover:bg-background-100 transition-colors whitespace-nowrap"
                  >
                    <i className="ri-external-link-line mr-1"></i>Ver original
                  </a>
                  <button
                    onClick={handleRemoveReceipt}
                    className="flex-1 text-xs px-3 py-2 border border-red-200 text-red-600 rounded-lg hover:bg-red-50 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-delete-bin-line mr-1"></i>Quitar
                  </button>
                </div>
              </div>
            ) : (
              <label className="flex items-center justify-center gap-2 w-full text-xs px-3 py-3 border border-dashed border-background-300 rounded-lg text-foreground-600 hover:border-accent-400 hover:text-accent-600 transition-colors cursor-pointer">
                <i className={uploading ? 'ri-loader-4-line animate-spin' : 'ri-upload-2-line'}></i>
                {uploading ? 'Subiendo...' : 'Subir imagen del comprobante'}
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleReceipt}
                  disabled={uploading}
                />
              </label>
            )}
          </div>

          {detail.notes && (
            <div>
              <p className="text-xs font-semibold text-foreground-700 mb-1">Notas</p>
              <p className="text-sm text-foreground-600">{detail.notes}</p>
            </div>
          )}

          {/* Cancelar compra */}
          {canCancel && (
            <div className="border-t border-background-200 pt-4">
              {confirmCancel ? (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 space-y-3">
                  <p className="text-xs text-red-700 leading-relaxed">
                    ¿Cancelar esta compra? Se <strong>revertirá el stock</strong> que había sumado al inventario y
                    quedará registrada como cancelada.
                  </p>
                  {cancelError && <p className="text-xs text-red-700">{cancelError}</p>}
                  <div className="flex gap-2">
                    <button
                      onClick={() => setConfirmCancel(false)}
                      className="flex-1 text-xs px-3 py-2 border border-background-300 text-foreground-700 rounded-lg hover:bg-white transition-colors whitespace-nowrap cursor-pointer"
                    >
                      No, volver
                    </button>
                    <button
                      onClick={handleCancel}
                      disabled={canceling}
                      className="flex-1 text-xs px-3 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors whitespace-nowrap font-medium cursor-pointer disabled:opacity-60"
                    >
                      {canceling ? (
                        <>
                          <i className="ri-loader-4-line animate-spin mr-1"></i>Cancelando...
                        </>
                      ) : (
                        'Sí, cancelar compra'
                      )}
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  onClick={() => setConfirmCancel(true)}
                  className="w-full text-xs px-4 py-2.5 border border-red-200 text-red-600 rounded-lg hover:bg-red-50 transition-colors whitespace-nowrap font-medium cursor-pointer"
                >
                  <i className="ri-close-circle-line mr-1.5"></i>Cancelar compra
                </button>
              )}
            </div>
          )}

          <div className="border-t border-background-200 pt-3">
            <button
              onClick={handleDownload}
              className="w-full text-sm px-4 py-2.5 border border-background-300 text-foreground-700 rounded-lg hover:bg-background-100 transition-colors whitespace-nowrap font-medium cursor-pointer"
            >
              <i className="ri-download-2-line mr-1.5"></i>Descargar comprobante
            </button>
          </div>
        </div>
      )}
    </Modal>
  );
}